import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { fmtDate } from "../lib/templates";

export default function RsvpTracker() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [guests, setGuests] = useState([]);
  const [bulkInput, setBulkInput] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    Promise.all([
      api.get(`/invitations/${id}`).then((r) => setInv(r.data)),
      api.get(`/invitations/${id}/rsvps`).then((r) => setRsvps(r.data)),
      api.get(`/invitations/${id}/rsvps/stats`).then((r) => setStats(r.data)),
      api.get(`/invitations/${id}/guests`).then((r) => setGuests(r.data)).catch(() => {}),
    ]);
  };

  useEffect(load, [id]);

  const addGuests = async () => {
    const lines = bulkInput.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((line) => {
      // accept: "Name, email@x.com" or "Name <email@x.com>" or just "email@x.com"
      const emailMatch = line.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
      if (!emailMatch) return null;
      const email = emailMatch[0];
      const name = line.replace(email, "").replace(/[<>,]/g, "").trim() || email.split("@")[0];
      return { name, email };
    }).filter(Boolean);
    if (parsed.length === 0) return toast.error("No valid emails found");
    const r = await api.post(`/invitations/${id}/guests`, { guests: parsed });
    toast.success(`${r.data.added} guest(s) added`);
    setBulkInput("");
    load();
  };

  const copyGuestLink = (g) => {
    const url = `${window.location.origin}/i/${inv.slug}?guest=${g.token}`;
    navigator.clipboard?.writeText(url);
    toast.success(`Personal link copied for ${g.name}`);
  };

  const delGuest = async (gid) => {
    await api.delete(`/invitations/${id}/guests/${gid}`);
    load();
  };

  const sendInvites = async () => {
    setSending(true);
    try {
      const r = await api.post(`/invitations/${id}/send-invites`);
      if (!r.data.configured) {
        toast.error("Email service not configured. Add Resend API key in backend.");
      } else {
        toast.success(`${r.data.sent} invite(s) sent · ${r.data.failed} failed`);
      }
      load();
    } catch (e) {
      toast.error("Failed to send invites");
    } finally {
      setSending(false);
    }
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const r = await api.post(`/invitations/${id}/send-reminders`);
      if (!r.data.configured) {
        toast.error("Email service not configured. Add Resend API key in backend.");
      } else {
        toast.success(`${r.data.sent} reminder(s) sent to ${r.data.pending_total} pending guests`);
      }
      load();
    } catch (e) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this RSVP?")) return;
    await api.delete(`/invitations/${id}/rsvps/${rid}`);
    toast.success("RSVP deleted");
    load();
  };

  const exportCsv = () => {
    const head = ["Name", "Email", "Phone", "Guests", "Attending", "Message", "Submitted"];
    const rows = rsvps.map((r) => [r.name, r.email || "", r.phone || "", r.guests, r.attending, (r.message || "").replace(/\n/g, " "), r.created_at]);
    const csv = [head, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rsvps-${inv?.slug || id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = rsvps.filter((r) => {
    if (filter !== "all" && r.attending !== filter) return false;
    if (query && !(r.name + " " + (r.email || "") + " " + (r.phone || "")).toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  if (!inv) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;

  const StatBox = ({ l, v, sub, accent = "#1A1918" }) => (
    <div className="bg-white border border-stone-200 p-5 md:p-6">
      <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{l}</div>
      <div className="font-serif-display text-3xl md:text-4xl mt-2" style={{ color: accent }}>{v}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="rsvp-tracker-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <Link to="/dashboard" className="text-xs uppercase tracking-[0.3em] text-stone-500 hover:text-black">← My Weddings</Link>
            <h1 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight mt-2">RSVP Tracker</h1>
            <div className="text-stone-600 mt-2">{inv.title} · <span className="font-mono-disp text-sm">/i/{inv.slug}</span></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/i/${inv.slug}`); toast.success("Public RSVP link copied"); }} data-testid="rsvp-copy-public-link" className="px-4 py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">📋 Copy Public Link</button>
            <Link to={`/qr/${id}`} data-testid="rsvp-qr-cards-link" target="_blank" rel="noreferrer" className="px-4 py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">📇 QR Cards</Link>
            <button onClick={load} className="px-4 py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100" data-testid="rsvp-refresh">Refresh</button>
            <button onClick={exportCsv} disabled={rsvps.length === 0} className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em] hover:bg-stone-800 disabled:opacity-50" data-testid="rsvp-export-csv">Export CSV</button>
          </div>
        </div>

        {/* HOW RSVP WORKS */}
        <div className="mb-8 bg-amber-50 border border-amber-200 p-5 md:p-6" data-testid="rsvp-help-banner">
          <div className="flex items-start gap-4">
            <div className="text-2xl">💡</div>
            <div className="flex-1">
              <div className="font-serif-display text-xl mb-1">How RSVP links work</div>
              <ol className="text-sm text-stone-700 space-y-1 list-decimal pl-5">
                <li><strong>Public link</strong> (top-right "📋 Copy Public Link") — share on WhatsApp/Instagram. Anyone can open and submit RSVP themselves.</li>
                <li><strong>Personalized link per guest</strong> — add guests below in "Guest List", then click the small "Copy Link" button beside each guest. That link auto-fills their name & email.</li>
                <li><strong>Email blast</strong> — once you've added a few guests, click "Send Invites" or "Send Reminders" to email them their personal link automatically.</li>
                <li>Replies appear in the table below in real time.</li>
              </ol>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-stone-600">
                <span className="font-medium">Public link:</span>
                <code className="bg-white border border-stone-200 px-2 py-0.5">{window.location.origin}/i/{inv.slug}</code>
              </div>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="rsvp-stats">
          <StatBox l="Total Responses" v={stats?.total_responses ?? "—"} />
          <StatBox l="Total Guests" v={stats?.total_guests ?? "—"} sub="Across all RSVPs" />
          <StatBox l="Attending" v={stats?.yes?.count ?? 0} sub={`${stats?.yes?.guests ?? 0} guests`} accent="#059669" />
          <StatBox l="Maybe" v={stats?.maybe?.count ?? 0} sub={`${stats?.maybe?.guests ?? 0} guests`} accent="#D97706" />
          <StatBox l="Not Attending" v={stats?.no?.count ?? 0} sub={`${stats?.no?.guests ?? 0} guests`} accent="#B91C1C" />
        </div>

        {/* FILTERS */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            data-testid="rsvp-search"
            placeholder="Search by name, email, phone..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm"
          />
          <div className="flex gap-1">
            {["all", "yes", "maybe", "no"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`rsvp-filter-${f}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${filter === f ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 hover:bg-stone-100"}`}
              >
                {f === "all" ? "All" : f === "yes" ? "Attending" : f === "no" ? "Declined" : "Maybe"}
              </button>
            ))}
          </div>
        </div>

        {/* LIST */}
        <div className="bg-white border border-stone-200">
          {filtered.length === 0 ? (
            <div className="p-16 text-center text-stone-500">
              {rsvps.length === 0 ? (
                <>
                  <div className="font-serif-display text-2xl mb-2">No RSVPs yet.</div>
                  <div>Share your invitation link and responses will appear here live.</div>
                  <div className="mt-4 px-4 py-2 inline-block bg-stone-100 font-mono-disp text-xs">{window.location.origin}/i/{inv.slug}</div>
                </>
              ) : (
                <div>No results match your filter.</div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-stone-200" data-testid="rsvp-list">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] uppercase tracking-[0.3em] text-stone-500 bg-stone-50">
                <div className="col-span-3">Guest</div>
                <div className="col-span-3">Contact</div>
                <div className="col-span-1 text-center">Guests</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Message</div>
                <div className="col-span-1 text-right">·</div>
              </div>
              {filtered.map((r) => {
                const color = r.attending === "yes" ? "#059669" : r.attending === "maybe" ? "#D97706" : "#B91C1C";
                const label = r.attending === "yes" ? "Attending" : r.attending === "maybe" ? "Maybe" : "Declined";
                return (
                  <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 hover:bg-stone-50" data-testid={`rsvp-row-${r.id}`}>
                    <div className="md:col-span-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-stone-500 md:hidden">{r.email || r.phone}</div>
                    </div>
                    <div className="md:col-span-3 text-sm hidden md:block">
                      {r.email && <div>{r.email}</div>}
                      {r.phone && <div className="text-stone-500">{r.phone}</div>}
                    </div>
                    <div className="md:col-span-1 md:text-center font-serif-display text-xl">{r.guests}</div>
                    <div className="md:col-span-2">
                      <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em]" style={{ background: `${color}15`, color }}>{label}</span>
                    </div>
                    <div className="md:col-span-2 text-sm text-stone-600 italic truncate" title={r.message || ""}>{r.message || "—"}</div>
                    <div className="md:col-span-1 md:text-right">
                      <button onClick={() => del(r.id)} data-testid={`rsvp-delete-${r.id}`} className="text-xs text-stone-400 hover:text-red-600 uppercase tracking-[0.2em]">Delete</button>
                    </div>
                    <div className="md:col-span-12 text-[10px] text-stone-400 mt-1">Submitted {fmtDate(r.created_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* GUEST LIST + EMAIL */}
        <div className="mt-12 bg-white border border-stone-200 p-6" data-testid="guest-list-panel">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <h2 className="font-serif-display text-3xl">Guest List & Email Reminders</h2>
              <div className="text-sm text-stone-500 mt-1">{guests.length} on list · {guests.filter(g => g.invited_at).length} invited · {guests.filter(g => g.rsvp_status && g.rsvp_status !== "pending").length} responded · {guests.filter(g => g.reminded_at).length} reminded</div>
            </div>
            <div className="flex gap-2">
              <button onClick={sendInvites} disabled={sending || guests.filter(g => !g.invited_at).length === 0} data-testid="send-invites-btn" className="px-4 py-2 bg-emerald-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-emerald-700 disabled:opacity-50">
                Send Invites ({guests.filter(g => !g.invited_at).length})
              </button>
              <button onClick={sendReminders} disabled={sending} data-testid="send-reminders-btn" className="px-4 py-2 bg-indigo-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 disabled:opacity-50">
                Send Reminders to Non-Responders
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Add guests (one per line · "Name, email@x.com" or just email)</div>
              <textarea
                data-testid="guest-bulk-input"
                rows={5}
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder={"Riya Kapoor, riya@example.com\nKarthik Iyer, karthik@example.com\nuncle.raj@example.com"}
                className="w-full px-3 py-2 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm font-mono-disp"
              />
              <button onClick={addGuests} data-testid="add-guests-btn" disabled={!bulkInput.trim()} className="mt-2 px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em] hover:bg-stone-800 disabled:opacity-50">+ Add Guests</button>
            </div>
            <div className="max-h-72 overflow-y-auto border border-stone-200" data-testid="guest-list">
              {guests.length === 0 ? (
                <div className="p-6 text-sm text-stone-500 text-center">No guests yet. Add some on the left to send invites + reminders.</div>
              ) : (
                guests.map((g) => {
                  const statusColor = g.rsvp_status === "yes" ? "#059669" : g.rsvp_status === "no" ? "#B91C1C" : g.rsvp_status === "maybe" ? "#D97706" : "#6B7280";
                  const statusLabel = g.rsvp_status === "yes" ? "Attending" : g.rsvp_status === "no" ? "Declined" : g.rsvp_status === "maybe" ? "Maybe" : "Pending";
                  return (
                    <div key={g.id} className="px-4 py-2 border-b border-stone-100 flex items-center justify-between text-sm last:border-b-0 gap-2" data-testid={`guest-row-${g.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{g.name}</div>
                        <div className="text-xs text-stone-500 truncate">{g.email}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5" style={{ background: `${statusColor}15`, color: statusColor }}>{statusLabel}</span>
                        {g.invited_at && <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 hidden lg:inline">Invited</span>}
                        {g.reminded_at && <span className="text-[10px] uppercase tracking-[0.2em] text-indigo-700 hidden lg:inline">Reminded</span>}
                        {g.token && (
                          <button onClick={() => copyGuestLink(g)} data-testid={`guest-copy-link-${g.id}`} title="Copy personalized RSVP link" className="text-[10px] uppercase tracking-[0.2em] text-stone-600 hover:text-stone-900 border border-stone-300 px-2 py-0.5 hover:bg-stone-100">
                            Copy Link
                          </button>
                        )}
                        <button onClick={() => delGuest(g.id)} data-testid={`guest-del-${g.id}`} className="text-stone-400 hover:text-red-600 text-lg leading-none">×</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="mt-4 text-xs text-stone-500 italic">Tip: A reminder is only sent to guests on the list whose email address has not yet appeared in an RSVP. Auto-send 7 days before the wedding can be wired up via a daily cron.</div>
        </div>
      </div>
    </div>
  );
}
